import { useState, useEffect, useRef } from "react";
import { MaterialEntry, Option } from "@/types/material";
import { normalizeText } from "@/utils/textFormatter";

interface UseMaterialFormArgs {
  formEntry: MaterialEntry;
  materialOptions: Option[];
  editing: boolean;
  onFormEntryChange: (entry: MaterialEntry) => void;
  onSave: () => void;
}

export const useMaterialForm = ({
  formEntry,
  materialOptions,
  editing,
  onFormEntryChange,
  onSave,
}: UseMaterialFormArgs) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userIsTyping, setUserIsTyping] = useState(false);
  const [search, setSearch] = useState(
    formEntry.materialType ? materialOptions.find((opt) => opt.value === formEntry.materialType)?.label ?? "" : ""
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [touched, setTouched] = useState({
    materialType: false,
    quantity: false,
  });
  const [materialTypeError, setMaterialTypeError] = useState("");
  const [quantityError, setQuantityError] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  // Reset touched state when the modal is opened with a new form entry
  useEffect(() => {
    if (!userIsTyping) {
      setTouched({ materialType: false, quantity: false });
      setMaterialTypeError("");
      setQuantityError(false);
      setSearch(
        formEntry.materialType ? materialOptions.find((opt) => opt.value === formEntry.materialType)?.label ?? "" : ""
      );
    }
  }, [formEntry.materialType, materialOptions, userIsTyping]);

    // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdownElement = dropdownRef.current;
      
      if (!dropdownElement) return;
      
      // Check if click is within the dropdown bounds
      const rect = dropdownElement.getBoundingClientRect();
      const isWithinDropdownBounds = event.clientX >= rect.left && event.clientX <= rect.right && 
                                   event.clientY >= rect.top && event.clientY <= rect.bottom;
      
      if (isWithinDropdownBounds) {
        return;
      }
      
      // Only close if click is truly outside and not currently scrolling
      if (!dropdownElement.contains(event.target as Node) && !isScrolling) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isScrolling]);

  // Filter options based on search and editing state
  const filteredOptions = materialOptions.filter(
    (option) => {
      const matchesSearch = normalizeText(option.label).includes(normalizeText(search)) || !search;
      const notAlreadySelected = !editing || option.value !== formEntry.materialType;
      return matchesSearch && notAlreadySelected;
    }
  );

  /** Handle material type search input change */
  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIsTyping(true);
    setSearch(e.target.value);
    setShowDropdown(true);

    if (!e.target.value) {
      onFormEntryChange({
        ...formEntry,
        materialType: "",
      });
    }
  };

  /** Handle material type selection */
  const handleTypeSelect = (option: Option, e: React.MouseEvent) => {
    e.preventDefault();
    setShowDropdown(false);
    setSearch(option.label);
    setUserIsTyping(false);
    onFormEntryChange({
      ...formEntry,
      materialType: option.value,
    });
    setTouched({ ...touched, materialType: true });
    setMaterialTypeError("");
  };

  /** Handle input click to show dropdown */
  const handleInputClick = () => {
    setShowDropdown(!showDropdown);
  };

  /** Handle material type blur */
  const handleTypeBlur = () => {
    setTouched({ ...touched, materialType: true });

    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        setUserIsTyping(false);
      }
    }, 200);
  };

  /** Handle unit selection change */
  const handleUnitChange = (value: string) => {
    onFormEntryChange({
      ...formEntry,
      unit: value,
    });
  };

  /** Handle quantity change */
  const handleQuantityChange = (value: string) => {
    onFormEntryChange({
      ...formEntry,
      quantity: value === "" ? "" : parseFloat(value),
    });
    setTouched({ ...touched, quantity: true });
    setQuantityError(false);
  };

  /** Make sure quantity is a valid number on blur */
  const handleQuantityBlur = () => {
    setTouched({ ...touched, quantity: true });

    if (formEntry.quantity === "") {
      setQuantityError(true);
      return;
    }

    const value = Number(formEntry.quantity);
    if (isNaN(value) || value <= 0) {
      setQuantityError(true);
      return;
    }

    setQuantityError(false);
  };

  /** Validate form before save */
  const handleSaveClick = (t: (key: string, ...args: any[]) => string) => {
    // Mark all fields as touched when attempting to save
    setTouched({ materialType: true, quantity: true });

    let valid = true;

    // Material type validation
    if (!formEntry.materialType) {
      setMaterialTypeError(t("validation.required"));
      valid = false;
    }

    // Quantity validation
    if (formEntry.quantity === "" || Number(formEntry.quantity) <= 0) {
      setQuantityError(true);
      valid = false;
    }

    if (valid) {
      onSave();
    }
  };

  // Check if form is valid for enabling/disabling save button
  const isFormValid = (): boolean => {
    if (!formEntry.materialType) return false;
    if (formEntry.quantity === "" || Number(formEntry.quantity) <= 0) return false;
    return true;
  };

  return {
    dropdownRef,
    search,
    showDropdown,
    materialTypeError,
    quantityError,
    touched,
    filteredOptions,
    isFormValid,
    isScrolling,
    setIsScrolling,
    handleTypeChange,
    handleTypeSelect,
    handleInputClick,
    handleTypeBlur,
    handleUnitChange,
    handleQuantityChange,
    handleQuantityBlur,
    handleSaveClick,
  };
};
